import { useCallback, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { AppSidebar } from '@/components/AppSidebar'
import { TopBar } from '@/components/TopBar'
import { useMarketContext } from '@/feed/useMarketContext'
import { orderStore } from '@/store/orders'
import { SimulationBanner } from '@/components/SimulationBanner'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'

/**
 * The app frame. Fixed viewport, no page scroll — panes scroll individually.
 *
 * `h-screen` + `overflow-hidden` rather than a scrolling document: a trading
 * terminal's chrome must never move, and an option chain that scrolls the
 * whole page takes the order ticket off screen mid-click. That is why the
 * provider is given an explicit height here: its own default is `min-h-svh`,
 * which lets the page grow past the viewport, and on this screen it must not.
 */

const COOKIE = 'sidebar_state'

/**
 * Was the sidebar left open?
 *
 * `SidebarProvider` writes this cookie itself but, being built for a server
 * -rendered app, expects the initial value to be handed to it. This is the
 * client-side equivalent: read once, before first paint, so the sidebar does
 * not render expanded and then snap shut.
 */
function wasOpen(): boolean {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE}=`))
    return hit === undefined ? true : hit.slice(COOKIE.length + 1) === 'true'
  } catch {
    // No cookies (a locked-down browser, a test environment): open is the
    // better default, because a collapsed rail with no way to discover the
    // labels is how the old fixed rail got it wrong.
    return true
  }
}

export function TerminalShell() {
  return (
    <SidebarProvider
      defaultOpen={wasOpen()}
      className="h-screen min-h-0 overflow-hidden bg-background text-foreground"
    >
      <ShellBody />
    </SidebarProvider>
  )
}

function ShellBody() {
  const market = useMarketContext()
  useEffect(() => { orderStore.reset() }, [market.sessionId])
  const { isMobile, open, openMobile, setOpen, setOpenMobile } = useSidebar()

  /**
   * Touching the content closes the sidebar.
   *
   * ───────────────────────────────────────────────────────────────────────
   * ON `pointerdown` CAPTURE, NOT ON CLICK
   *
   * The sidebar is 16rem of a terminal that needs every pixel, so reaching
   * into the chain is itself the signal that you are done with the nav. The
   * CAPTURE phase is what makes that feel immediate rather than fighting
   * the click: it runs before the target's own handler, so the panes have
   * already begun reflowing by the time the strike you pressed responds.
   *
   * Focus too, because a keyboard user tabbing into the chain has left the
   * sidebar just as definitely as a pointer has.
   * ───────────────────────────────────────────────────────────────────────
   */
  const closeFromContent = useCallback(() => {
    if (isMobile) {
      if (openMobile) setOpenMobile(false)
      return
    }
    if (open) setOpen(false)
  }, [isMobile, open, openMobile, setOpen, setOpenMobile])

  return (
    <>
      <AppSidebar />

      <SidebarInset className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        {/*
          On a phone the sidebar is a sheet, so the only way back to it is a
          trigger in the content. On a desktop the sidebar has its own.
        */}
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3 lg:hidden">
          <SidebarTrigger />
        </header>

        <TopBar />
        <SimulationBanner />

        <main
          className="min-h-0 flex-1 overflow-hidden"
          onPointerDownCapture={closeFromContent}
          onFocusCapture={closeFromContent}
        >
          <Outlet key={market.sessionId ?? 'normal'} />
        </main>
      </SidebarInset>
    </>
  )
}
